// ─── Surface Material — generic floating-element visual style ───────────────
//
// First consumer: Response Card (RSVP). Future consumers: language selector,
// timeline cards, location/accommodation cards, dropdowns, modals, guest
// widgets, and other floating template elements.
//
// This module is intentionally decoupled from any particular component's
// data shape. Callers translate their own state (e.g. `customStyle.card*`)
// into the generic args this helper accepts.
//
// Rendering rules:
//   - Opacity / blur / saturation are applied to the SURFACE LAYER only.
//     Content (text, fields, buttons, icons, validation) must remain fully
//     opaque. Never use parent `opacity`.
//   - Border alpha is multiplied by surfaceAlpha so cards fade out cleanly
//     in floating mode.
//   - Backdrop-filter is set together with `-webkit-backdrop-filter` and
//     `will-change` for smooth scroll perf.

/**
 * Convert a CSS color (#rgb, #rrggbb, rgb(), rgba()) to rgba() with the
 * given alpha (0..1). Returns 'transparent' if alpha is 0. Falls back to
 * the original string if it can't parse (named colors, currentColor, var()).
 */
export function withAlpha(color, alpha) {
  if (alpha >= 1) return color;
  if (alpha <= 0) return 'transparent';
  if (!color || typeof color !== 'string') return color;
  const c = color.trim();

  if (c.startsWith('#')) {
    let h = c.slice(1);
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    if (h.length === 6 || h.length === 8) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      if ([r, g, b].every((v) => Number.isFinite(v))) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }
  }

  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map((s) => s.trim());
    if (parts.length >= 3) {
      const r = parts[0];
      const g = parts[1];
      const b = parts[2];
      const baseA = parts[3] !== undefined ? parseFloat(parts[3]) : 1;
      const finalA = (Number.isFinite(baseA) ? baseA : 1) * alpha;
      return `rgba(${r}, ${g}, ${b}, ${finalA})`;
    }
  }

  return color;
}

function clamp(v, lo, hi) {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : lo;
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Returns a CSS-in-JS style object describing a floating element's
 * "surface material" — background fill, border, soft drop shadow, and
 * backdrop blur/saturate (Liquid Glass).
 *
 * All inputs are scalars with sane defaults; missing/undefined inputs
 * produce a no-op (legacy-safe). The returned style object is meant to be
 * spread onto the surface element only — never the parent.
 *
 * @param {Object} args
 * @param {number} [args.surfaceOpacity=100]        0..100 — background alpha
 * @param {number} [args.surfaceShadowStrength=0]   0..100 — soft drop shadow
 * @param {number} [args.backdropBlur=0]            0..30  — px of backdrop blur
 * @param {number} [args.backdropSaturate=100]      100..200 — backdrop saturation %
 * @param {string} [args.backgroundColor]           base color (any CSS color)
 * @param {string} [args.borderColor]               base border color
 * @returns {Object} CSS style object
 */
export function getSurfaceMaterialStyle({
  surfaceOpacity = 100,
  surfaceShadowStrength = 0,
  backdropBlur = 0,
  backdropSaturate = 100,
  backgroundColor,
  borderColor,
} = {}) {
  const alpha = clamp(surfaceOpacity, 0, 100) / 100;
  const shadow = clamp(surfaceShadowStrength, 0, 100);
  const blur = clamp(backdropBlur, 0, 30);
  const saturate = clamp(backdropSaturate, 100, 200);

  const style = {};

  if (backgroundColor !== undefined && backgroundColor !== null) {
    style.backgroundColor = alpha >= 1 ? backgroundColor : withAlpha(backgroundColor, alpha);
  }

  if (borderColor !== undefined && borderColor !== null) {
    style.borderColor = alpha >= 1 ? borderColor : withAlpha(borderColor, alpha);
  }

  if (shadow > 0) {
    // Editorial soft shadow ramp:
    //   strength=0   → none
    //   strength=20  → 0 8px 28px rgba(0,0,0,0.04) — stationery lift
    //   strength=50  → 0 14px 40px rgba(0,0,0,0.09) — vellum float
    //   strength=100 → 0 24px 60px rgba(0,0,0,0.18) — editorial card
    // Shadow alpha is multiplied by the layer's alpha so opacity=0 →
    // shadow=0 (the whole element fades together, including its shadow).
    const y = 4 + shadow * 0.2;
    const blurPx = 20 + shadow * 0.4;
    const shadowAlpha = shadow * 0.0018 * alpha;
    if (shadowAlpha > 0) {
      style.boxShadow = `0 ${y.toFixed(1)}px ${blurPx.toFixed(1)}px rgba(0, 0, 0, ${shadowAlpha.toFixed(3)})`;
    }
  }

  if (blur > 0 || saturate !== 100) {
    const filter = `blur(${blur}px) saturate(${saturate}%)`;
    style.backdropFilter = filter;
    style.WebkitBackdropFilter = filter;
    style.willChange = 'backdrop-filter';
  }

  return style;
}
